import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { faTimes, faCheck, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from './Modal';
import Button from './Button';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropperProps {
  isOpen: boolean;
  onClose: () => void;
  onCropComplete: (croppedImageBlob: Blob) => void;
  imageFile: File;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  isOpen,
  onClose,
  onCropComplete,
  imageFile
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [loading, setLoading] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    if (imageFile && isOpen) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(imageFile);
    }

    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageFile, imageSrc, isOpen]);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const size = Math.min(width, height);
    const x = (width - size) / 2;
    const y = (height - size) / 2;

    setCrop({
      unit: 'px',
      width: size * 0.8,
      height: size * 0.8,
      x: x + size * 0.1,
      y: y + size * 0.1
    });
  }, []);

  const getCroppedImg = useCallback(async (): Promise<Blob | null> => {
    if (!completedCrop || !imgRef.current) return null;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Set canvas size to square
    const size = Math.min(completedCrop.width, completedCrop.height) * Math.min(scaleX, scaleY);
    canvas.width = size;
    canvas.height = size;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      size,
      size
    );

    return new Promise(resolve => {
      canvas.toBlob(
        blob => {
          resolve(blob);
        },
        'image/png',
        0.9
      );
    });
  }, [completedCrop]);

  const handleCropComplete = async () => {
    setLoading(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        onCropComplete(croppedBlob);
        onClose();
      }
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setLoading(false);
    }
  };

  const makeSquareCrop = (crop: Crop): Crop => {
    const size = Math.min(crop.width || 0, crop.height || 0);
    return {
      ...crop,
      width: size,
      height: size
    };
  };

  const handleCropChange = (newCrop: Crop) => {
    setCrop(makeSquareCrop(newCrop));
  };

  const footerContent = (
    <div className="flex justify-end space-x-3">
      <Button onClick={onClose} variant="secondary" leftIcon={faTimes}>
        Cancelar
      </Button>
      <Button
        onClick={handleCropComplete}
        variant="primary"
        leftIcon={faCheck}
        loading={loading}
        disabled={loading || !completedCrop}
      >
        {loading ? 'Processando...' : 'Cortar Imagem'}
      </Button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cortar Imagem" footer={footerContent}>
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          Ajuste a área de corte para criar um logo quadrado. A imagem será cortada automaticamente
          para manter as proporções corretas.
        </div>

        {imageSrc && (
          <div className="flex justify-center">
            <ReactCrop
              crop={crop}
              onChange={handleCropChange}
              onComplete={setCompletedCrop}
              aspect={1}
              minWidth={50}
              minHeight={50}
              keepSelection
            >
              <img
                ref={imgRef}
                alt="Crop preview"
                src={imageSrc}
                onLoad={onImageLoad}
                className="max-w-full max-h-96 object-contain"
              />
            </ReactCrop>
          </div>
        )}

        <div className="text-xs text-gray-500 text-center">
          <FontAwesomeIcon icon={faRotateRight} className="mr-1" />
          Dica: Arraste as bordas para ajustar a área de corte
        </div>
      </div>
    </Modal>
  );
};

export default ImageCropper;
